import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Download,
  ExternalLink,
  LayoutList,
  Loader2,
  Map as MapIcon,
  Search,
} from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  CircleMarker,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import {
  RecipientsFilter,
  type FilterItem,
  type FilterProductOption,
} from "@/routes/MessagesSMSCompose/RecipientsFilter";
import { DEFAULT_FILTERS_OPERATOR, filterItemToApiFilter, type FiltersOperator } from "@/lib/contactFilterApi";
import {
  CONTACTS_SEARCH_COLUMNS,
  contactsSearchColumnLabel,
} from "@/lib/contactsSearchLeadFields";
import { instagramProfileUrlFromHandle } from "@/lib/instagramProfileUrl";

const LEAD_COLUMN_ORDER = CONTACTS_SEARCH_COLUMNS;

function getLeadCellValue(
  row: Record<string, unknown>,
  column: string
): unknown {
  if (column === "instagram.igUserId" || column === "instagram.igUsername") {
    const ig = row.instagram;
    if (ig != null && typeof ig === "object") {
      const sub = column === "instagram.igUserId" ? "igUserId" : "igUsername";
      return (ig as Record<string, unknown>)[sub];
    }
    return undefined;
  }
  return row[column];
}

function columnKeys(leads: Record<string, unknown>[]): string[] {
  const keys = new Set<string>();
  for (const l of leads) {
    for (const k of Object.keys(l)) keys.add(k);
  }
  if (keys.has("instagram")) {
    keys.delete("instagram");
    keys.add("instagram.igUserId");
    keys.add("instagram.igUsername");
  }
  return LEAD_COLUMN_ORDER.filter((k) => keys.has(k));
}

function formatCell(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatContactsSearchCell(column: string, value: unknown): string {
  if (
    column === "numPurchases" &&
    typeof value === "number" &&
    Number.isFinite(value)
  ) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  if (
    column === "amountSpent" &&
    typeof value === "number" &&
    Number.isFinite(value)
  ) {
    const dollars = value / 100;
    return `$${dollars.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  if (column === "lastActive") {
    if (value == null || value === "") return "—";
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return formatCell(value);
}

function leadLatLng(row: Record<string, unknown>): { lat: number; lng: number } | null {
  const lat = row.lat;
  const lng = row.lng;
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

function leadLabel(row: Record<string, unknown>): string {
  const name = row.name;
  if (typeof name === "string" && name.trim()) return name;
  const fn = row.firstName;
  const ln = row.lastName;
  const parts = [
    typeof fn === "string" ? fn : "",
    typeof ln === "string" ? ln : "",
  ].filter(Boolean);
  if (parts.length) return parts.join(" ");
  const email = row.email;
  if (typeof email === "string" && email) return email;
  return String(row._id ?? "Contact");
}

const INDIVIDUAL_CONTACT_ZOOM = 11;

type LeadMapPoint = { row: Record<string, unknown>; lat: number; lng: number };

type LeadMapCluster =
  | { kind: "single"; point: LeadMapPoint }
  | { kind: "cluster"; lat: number; lng: number; points: LeadMapPoint[] };

/** Merge radius in degrees — wider cells when zoomed out, tighter as zoom increases */
function clusterGridDegrees(zoom: number): number {
  const z = Math.max(2, Math.min(zoom, INDIVIDUAL_CONTACT_ZOOM - 1));
  return 360 / 2 ** (z + 3.5);
}

function clusterLeadPoints(points: LeadMapPoint[], zoom: number): LeadMapCluster[] {
  if (points.length <= 1 || zoom >= INDIVIDUAL_CONTACT_ZOOM) {
    return points.map((point) => ({ kind: "single", point }));
  }

  const gridDegrees = clusterGridDegrees(zoom);
  const buckets = new Map<string, LeadMapPoint[]>();

  for (const point of points) {
    const latCell = Math.floor(point.lat / gridDegrees);
    const lngCell = Math.floor(point.lng / gridDegrees);
    const key = `${latCell}:${lngCell}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(point);
    else buckets.set(key, [point]);
  }

  const clusters: LeadMapCluster[] = [];
  for (const bucket of buckets.values()) {
    if (bucket.length === 1) {
      clusters.push({ kind: "single", point: bucket[0] });
      continue;
    }
    let latSum = 0;
    let lngSum = 0;
    for (const p of bucket) {
      latSum += p.lat;
      lngSum += p.lng;
    }
    clusters.push({
      kind: "cluster",
      lat: latSum / bucket.length,
      lng: lngSum / bucket.length,
      points: bucket,
    });
  }
  return clusters;
}

function clusterCountIcon(count: number): L.DivIcon {
  return L.divIcon({
    className: "contact-map-cluster-icon",
    html: `<span class="contact-map-cluster-marker">${count.toLocaleString()}</span>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

function leadMapPointKey(point: LeadMapPoint): string {
  return String(point.row._id ?? `${point.lat},${point.lng}`);
}

function ContactMapMarkers({ leadsWithCoords }: { leadsWithCoords: LeadMapPoint[] }) {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());

  useEffect(() => {
    const syncZoom = () => setZoom(map.getZoom());
    map.on("zoomend", syncZoom);
    map.on("moveend", syncZoom);
    syncZoom();
    return () => {
      map.off("zoomend", syncZoom);
      map.off("moveend", syncZoom);
    };
  }, [map]);

  const clusters = useMemo(
    () => clusterLeadPoints(leadsWithCoords, zoom),
    [leadsWithCoords, zoom]
  );

  const zoomToCluster = useCallback(
    ({ points }: { points: LeadMapPoint[] }) => {
      if (points.length === 1) {
        const p = points[0];
        map.flyTo([p.lat, p.lng], Math.min(map.getZoom() + 3, INDIVIDUAL_CONTACT_ZOOM + 1));
        return;
      }
      const bounds = L.latLngBounds(
        points.map((p) => [p.lat, p.lng] as [number, number])
      );
      map.flyToBounds(bounds, {
        padding: [48, 48],
        maxZoom: INDIVIDUAL_CONTACT_ZOOM + 1,
      });
    },
    [map]
  );

  return (
    <>
      {clusters.map((entry) => {
        if (entry.kind === "single") {
          const { row, lat, lng } = entry.point;
          return (
            <CircleMarker
              key={leadMapPointKey(entry.point)}
              center={[lat, lng]}
              radius={8}
              pathOptions={{
                color: "var(--primary)",
                fillColor: "var(--primary)",
                fillOpacity: 0.85,
                weight: 2,
              }}
            >
              <Popup>
                {row._id != null && String(row._id) !== "" ? (
                  <Link
                    to={`/contacts/${String(row._id)}/edit`}
                    className="inline-flex items-center gap-1.5 text-sm text-primary underline underline-offset-4"
                  >
                    {leadLabel(row)}
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  </Link>
                ) : (
                  <span className="text-sm">{leadLabel(row)}</span>
                )}
              </Popup>
            </CircleMarker>
          );
        }

        const count = entry.points.length;
        const clusterKey = entry.points.map(leadMapPointKey).sort().join("|");
        return (
          <Marker
            key={`cluster-${clusterKey}`}
            position={[entry.lat, entry.lng]}
            icon={clusterCountIcon(count)}
            eventHandlers={{
              click: () => zoomToCluster({ points: entry.points }),
            }}
          >
            <Popup>
              <p className="text-sm font-medium tabular-nums">
                {count.toLocaleString()} contacts
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Click the marker to zoom in
              </p>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 10);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [28, 28], maxZoom: 14 });
  }, [map, points]);
  return null;
}

function ContactsSearchMapView({
  leadsWithCoords,
  isLoading,
  hasSearched,
  totalWithCoords,
  totalResults,
}: {
  leadsWithCoords: { row: Record<string, unknown>; lat: number; lng: number }[];
  isLoading: boolean;
  hasSearched: boolean;
  totalWithCoords: number;
  totalResults: number;
}) {
  const fitPoints = useMemo(
    () => leadsWithCoords.map((p) => [p.lat, p.lng] as [number, number]),
    [leadsWithCoords]
  );
  const first: [number, number] = fitPoints[0] ?? [20, 0];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }
  if (!hasSearched) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        Run a search to load contacts on the map.
      </p>
    );
  }
  if (totalResults === 0) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        No contacts match these filters.
      </p>
    );
  }
  if (totalWithCoords === 0) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        No contacts in these results have coordinates (lat/lng).
      </p>
    );
  }

  return (
    <div className="relative h-[min(60vh,560px)] w-full overflow-hidden rounded-md border [&_.contact-map-cluster-icon]:!border-0 [&_.contact-map-cluster-icon]:!bg-transparent [&_.contact-map-cluster-marker]:flex [&_.contact-map-cluster-marker]:size-10 [&_.contact-map-cluster-marker]:items-center [&_.contact-map-cluster-marker]:justify-center [&_.contact-map-cluster-marker]:rounded-full [&_.contact-map-cluster-marker]:border-2 [&_.contact-map-cluster-marker]:border-background [&_.contact-map-cluster-marker]:bg-primary [&_.contact-map-cluster-marker]:text-sm [&_.contact-map-cluster-marker]:font-semibold [&_.contact-map-cluster-marker]:text-primary-foreground [&_.contact-map-cluster-marker]:shadow-md [&_.leaflet-container]:z-0 [&_.leaflet-container]:h-full [&_.leaflet-container]:w-full">
      <MapContainer
        center={first}
        zoom={4}
        scrollWheelZoom
        className="h-full min-h-[320px] w-full"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={fitPoints} />
        <ContactMapMarkers leadsWithCoords={leadsWithCoords} />
      </MapContainer>
    </div>
  );
}

function csvEscape(value: unknown): string {
  if (value == null) return "";
  const s =
    typeof value === "object" ? JSON.stringify(value) : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function compareLeadValues({
  a,
  b,
  column,
}: {
  a: unknown;
  b: unknown;
  column: string;
}): number {
  const empty = (v: unknown) => v == null || v === "";
  if (empty(a) && empty(b)) return 0;
  if (empty(a)) return 1;
  if (empty(b)) return -1;

  if (
    column.endsWith("At") ||
    column === "joinedAt" ||
    column === "createdAt" ||
    column === "updatedAt" ||
    column === "lastActive"
  ) {
    const ta = new Date(String(a)).getTime();
    const tb = new Date(String(b)).getTime();
    if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;
  }
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean")
    return a === b ? 0 : a ? 1 : -1;
  const sa = typeof a === "object" ? JSON.stringify(a) : String(a);
  const sb = typeof b === "object" ? JSON.stringify(b) : String(b);
  return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: "base" });
}

function noop() {}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export function ContactsSearch() {
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [filters, setFilters] = useState<FilterItem[]>([]);
  const [filtersOperator, setFiltersOperator] = useState<FiltersOperator>(DEFAULT_FILTERS_OPERATOR);
  const [searchFilters, setSearchFilters] = useState<FilterItem[] | null>(null);
  const [searchFiltersOperator, setSearchFiltersOperator] = useState<FiltersOperator | null>(null);
  const [pages, setPages] = useState<{ _id: string; title: string; route: string }[]>(
    []
  );
  const [groups, setGroups] = useState<
    { _id: string; name: string; contacts?: number }[]
  >([]);
  const [products, setProducts] = useState<FilterProductOption[]>([]);
  const [leads, setLeads] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(25);
  const [resultView, setResultView] = useState<"list" | "map">("list");

  const hasPageFilter = filters.some((f) => f.filterType === "page");
  const hasGroupFilter = filters.some((f) => f.filterType === "contactGroup");
  const hasProductFilter = filters.some((f) => f.filterType === "product");

  useEffect(() => {
    setBreadcrumbs([
      { label: "Contacts", path: "/contacts" },
      { label: "Search", path: "/contacts/search" },
    ]);
    return () => clearBreadcrumbs();
  }, [setBreadcrumbs, clearBreadcrumbs]);

  useEffect(() => {
    if (hasPageFilter) {
      fetch("/api/pages")
        .then((r) => r.json())
        .then((data) => setPages(data.pages || []))
        .catch(() => setPages([]));
    }
  }, [hasPageFilter]);

  useEffect(() => {
    if (hasGroupFilter) {
      fetch("/api/lead-groups")
        .then((r) => r.json())
        .then((data) => setGroups(data.groups || []))
        .catch(() => setGroups([]));
    }
  }, [hasGroupFilter]);

  useEffect(() => {
    if (hasProductFilter) {
      fetch("/api/products")
        .then((r) => r.json())
        .then((data) => setProducts(data.products || []))
        .catch(() => setProducts([]));
    }
  }, [hasProductFilter]);

  useEffect(() => {
    if (searchFilters === null) {
      return;
    }
    const run = async () => {
      const apiFilters = searchFilters
        .map(filterItemToApiFilter)
        .filter((f): f is Record<string, unknown> => f != null);

      setIsLoading(true);
      try {
        const filtersParam = encodeURIComponent(JSON.stringify(apiFilters));
        const operatorParam =
          searchFiltersOperator === "or" ? "&filtersOperator=or" : "";
        const res = await fetch(
          `/api/contacts/search?filters=${filtersParam}${operatorParam}`
        );
        if (res.ok) {
          const data = await res.json();
          setLeads(Array.isArray(data.leads) ? data.leads : []);
        } else {
          setLeads([]);
        }
      } catch {
        setLeads([]);
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [searchFilters, searchFiltersOperator]);

  useEffect(() => {
    setPage(1);
  }, [searchFilters]);

  const applySearch = useCallback(() => {
    setSearchFilters(structuredClone(filters));
    setSearchFiltersOperator(filtersOperator);
  }, [filters, filtersOperator]);

  const filtersMatchSearch =
    searchFilters !== null &&
    searchFiltersOperator !== null &&
    JSON.stringify(filters) === JSON.stringify(searchFilters) &&
    filtersOperator === searchFiltersOperator;

  const allCols = useMemo(() => columnKeys(leads), [leads]);

  useEffect(() => {
    setHiddenColumns((prev) => {
      const next = new Set(
        [...prev].filter((k) => allCols.includes(k))
      );
      return next.size === prev.size ? prev : next;
    });
  }, [allCols]);

  const visibleCols = useMemo(
    () => allCols.filter((c) => !hiddenColumns.has(c)),
    [allCols, hiddenColumns]
  );

  useEffect(() => {
    if (sortColumn && !visibleCols.includes(sortColumn)) {
      setSortColumn(null);
    }
  }, [sortColumn, visibleCols]);

  const sortedLeads = useMemo(() => {
    if (!sortColumn) return leads;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...leads].sort((rowA, rowB) => {
      const cmp = compareLeadValues({
        a: getLeadCellValue(rowA, sortColumn),
        b: getLeadCellValue(rowB, sortColumn),
        column: sortColumn,
      });
      return cmp * dir;
    });
  }, [leads, sortColumn, sortDirection]);

  const leadsWithCoords = useMemo(() => {
    const out: { row: Record<string, unknown>; lat: number; lng: number }[] = [];
    for (const row of sortedLeads) {
      const ll = leadLatLng(row);
      if (ll) out.push({ row, lat: ll.lat, lng: ll.lng });
    }
    return out;
  }, [sortedLeads]);

  const totalCount = sortedLeads.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const displayPage = Math.min(Math.max(1, page), totalPages);

  const paginatedLeads = useMemo(() => {
    const start = (displayPage - 1) * pageSize;
    return sortedLeads.slice(start, start + pageSize);
  }, [sortedLeads, displayPage, pageSize]);

  const rangeStart = totalCount === 0 ? 0 : (displayPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(displayPage * pageSize, totalCount);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn]
  );

  const toggleColumnHidden = useCallback(({ key }: { key: string }) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        return next;
      }
      const visible = allCols.filter((c) => !next.has(c));
      if (visible.length <= 1) return prev;
      next.add(key);
      return next;
    });
  }, [allCols]);

  const exportCsv = useCallback(() => {
    const cols = visibleCols.length > 0 ? visibleCols : allCols;
    const header = cols.map((c) => contactsSearchColumnLabel(c)).join(",");
    const lines = sortedLeads.map((row) =>
      cols
        .map((c) =>
          csvEscape(formatContactsSearchCell(c, getLeadCellValue(row, c)))
        )
        .join(",")
    );
    const blob = new Blob([[header, ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [allCols, sortedLeads, visibleCols]);

  return (
    <div className="p-4 max-w-[min(100vw-2rem,1400px)] mx-auto w-full space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">Contact search</h1>
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={allCols.length === 0}
              >
                <Columns3 className="h-4 w-4 mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[min(70vh,24rem)] overflow-y-auto">
              <DropdownMenuLabel>Show columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {allCols.map((key) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  checked={!hiddenColumns.has(key)}
                  onCheckedChange={() => toggleColumnHidden({ key })}
                  onSelect={(e) => e.preventDefault()}
                >
                  {contactsSearchColumnLabel(key)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            type="button"
            variant="secondary"
            disabled={leads.length === 0}
            onClick={exportCsv}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <RecipientsFilter
        filters={filters}
        onFiltersChange={setFilters}
        filtersOperator={filtersOperator}
        onFiltersOperatorChange={setFiltersOperator}
        pages={pages}
        groups={groups}
        products={products}
        leads={[]}
        selectedLeadIds={new Set()}
        onSelectLead={noop}
        onSelectAll={noop}
        showLeadPicker={false}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          onClick={applySearch}
          disabled={isLoading || filtersMatchSearch}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Search
        </Button>
        {searchFilters === null ? (
          <span className="text-sm text-muted-foreground">
            Click Search to load results.
          </span>
        ) : (
          !filtersMatchSearch && (
            <span className="text-sm text-muted-foreground">
              Filters changed — run search to update results.
            </span>
          )
        )}
      </div>

      <Tabs
        value={resultView}
        onValueChange={(v) => setResultView(v as "list" | "map")}
        className="gap-4"
      >
        <TabsList className="w-fit">
          <TabsTrigger value="list" className="gap-1.5">
            <LayoutList className="h-4 w-4" />
            List
          </TabsTrigger>
          <TabsTrigger value="map" className="gap-1.5">
            <MapIcon className="h-4 w-4" />
            Map
          </TabsTrigger>
        </TabsList>

        <div className="space-y-2 pt-2">
          <p className="text-sm text-muted-foreground">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
                Loading…
              </>
            ) : searchFilters === null ? (
              <>No results loaded yet.</>
            ) : (
              <>
                {totalCount.toLocaleString()} contact{totalCount === 1 ? "" : "s"}
                {totalCount > 0 && resultView === "list" && (
                  <span className="text-muted-foreground">
                    {" "}
                    · showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}
                  </span>
                )}
                {totalCount > 0 && resultView === "map" && leadsWithCoords.length > 0 && (
                  <span className="text-muted-foreground">
                    {" "}
                    · {leadsWithCoords.length.toLocaleString()} with location
                  </span>
                )}
              </>
            )}
          </p>

          <TabsContent value="list" className="mt-0 space-y-2 outline-none">
        <div className="border rounded-md overflow-x-auto max-h-[min(70vh,900px)] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 shrink-0" aria-label="Open contact editor" />
                {(visibleCols.length > 0 ? visibleCols : allCols).map((c) => (
                  <TableHead
                    key={c}
                    className="whitespace-nowrap min-w-[8rem] cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort(c)}
                  >
                    <div className="flex items-center gap-1">
                      {contactsSearchColumnLabel(c)}
                      {sortColumn === c ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        ) : (
                          <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {!isLoading && leads.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(visibleCols.length || allCols.length, 1) + 1}
                    className="text-center text-muted-foreground"
                  >
                    {searchFilters === null
                      ? "Run a search to load contacts."
                      : "No contacts match these filters."}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedLeads.map((row) => {
                  const id = String(row._id ?? "");
                  const rowCols = visibleCols.length > 0 ? visibleCols : allCols;
                  return (
                    <TableRow key={id}>
                      <TableCell className="w-10 shrink-0 align-top p-2">
                        {id !== "" ? (
                          <Link
                            to={`/contacts/${id}/edit`}
                            className="inline-flex shrink-0 items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label="Edit contact"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        ) : null}
                      </TableCell>
                      {rowCols.map((c) => {
                        const cell = getLeadCellValue(row, c);
                        const display = formatContactsSearchCell(c, cell);
                        const igProfileUrl =
                          c === "instagram.igUsername" &&
                          typeof cell === "string" &&
                          cell.trim()
                            ? instagramProfileUrlFromHandle(cell)
                            : null;
                        const emailRaw =
                          c === "email" && typeof cell === "string" ? cell.trim() : "";
                        const phoneRaw =
                          c === "phoneNumber" && typeof cell === "string" ? cell.trim() : "";
                        const cellInner =
                          igProfileUrl != null ? (
                            <a
                              href={igProfileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary underline underline-offset-4"
                            >
                              {display}
                            </a>
                          ) : emailRaw ? (
                            <a
                              href={`mailto:${encodeURIComponent(emailRaw)}`}
                              className="text-primary underline underline-offset-4"
                            >
                              {display}
                            </a>
                          ) : phoneRaw ? (
                            <Link
                              to={`/messages/sms/inbox/${encodeURIComponent(phoneRaw)}`}
                              className="text-primary underline underline-offset-4"
                            >
                              {display}
                            </Link>
                          ) : (
                            display
                          );
                        return (
                          <TableCell
                            key={c}
                            className="max-w-[20rem] truncate align-top text-sm"
                            title={display}
                          >
                            {cellInner}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {!isLoading && totalCount > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="whitespace-nowrap">Rows per page</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v) as (typeof PAGE_SIZE_OPTIONS)[number]);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[4.5rem] h-8" aria-label="Rows per page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground tabular-nums">
                Page {displayPage.toLocaleString()} of {totalPages.toLocaleString()}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={displayPage <= 1}
                onClick={() => setPage(displayPage - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={displayPage >= totalPages}
                onClick={() => setPage(displayPage + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
          </TabsContent>

          <TabsContent value="map" className="mt-0 outline-none">
            {resultView === "map" && (
              <ContactsSearchMapView
                leadsWithCoords={leadsWithCoords}
                isLoading={isLoading}
                hasSearched={searchFilters !== null}
                totalWithCoords={leadsWithCoords.length}
                totalResults={totalCount}
              />
            )}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
